// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { ViewContext } from '../viewContext';
import type { NameRec, SeriesKey } from '../types';
import { histogramBins } from '../analysis';
import { histogramChart } from '../charts';
import { SEX_COLOUR } from '../colours';
import { escapeHtml, formatNumber } from '../utils/format';
import { segHtml, viewHead, wireNameRows } from './shared';

type SortKey = 'name' | 'best' | 'total' | 'first';

function sexesOf(n: NameRec): { boy: boolean; girl: boolean } {
  let boy = false;
  let girl = false;
  for (const k of Object.keys(n.series) as SeriesKey[]) {
    if (k.endsWith('boy')) boy = true;
    if (k.endsWith('girl')) girl = true;
  }
  return { boy, girl };
}

function typeLabel(n: NameRec): string {
  const s = sexesOf(n);
  if (s.boy && s.girl) return '<span class="pill" style="background:#8a6bbf">both</span>';
  if (s.boy) return `<span class="pill" style="background:${SEX_COLOUR.boy}">boy</span>`;
  return `<span class="pill" style="background:${SEX_COLOUR.girl}">girl</span>`;
}

export function renderExplorer(root: HTMLElement, ctx: ViewContext): void {
  const { names } = ctx.data;
  let sortKey: SortKey = 'total';
  let sortDir: 1 | -1 = -1;
  let query = '';
  let typeFilter: 'all' | 'boy' | 'girl' | 'both' = 'all';
  let binFilter: [number, number] | null = null;

  root.innerHTML = `
    ${viewHead(
      'Explorer',
      'Every name in the dataset, searchable and sortable. The histogram shows when each name first entered a top-100 list — the tall first bar is the names already popular in 1952, and fresh names have kept arriving in every decade since. Click a bar to filter, or any row to open the name.',
    )}
    <div class="panel">
      <div class="panel-title">When names first reached the top 100</div>
      <div class="panel-sub" data-role="hist-sub">Each name counted once, by the first year it appeared. Click a bar to filter the table below.</div>
      <div class="chart-scroll" data-role="hist"></div>
    </div>
    <div class="panel">
      <div class="controls">
        <input class="text-input" data-role="q" type="search" placeholder="Search names…" aria-label="Search names" style="width:min(240px,70vw)" />
        <span class="field-label" style="margin-left:var(--space-md)">Type:</span>
        ${segHtml('Type', [
          { id: 'all', label: 'All' },
          { id: 'girl', label: 'Girls' },
          { id: 'boy', label: 'Boys' },
          { id: 'both', label: 'Unisex' },
        ], typeFilter)}
        <span class="field-label" data-role="count" style="margin-left:auto"></span>
      </div>
      <div class="table-scroll">
        <table class="data">
          <thead>
            <tr>
              <th class="sortable" data-sort="name">Name</th>
              <th>Type</th>
              <th class="sortable t-right" data-sort="best">Best rank</th>
              <th class="t-right">Peak year</th>
              <th class="sortable t-right" data-sort="total">Total babies</th>
              <th class="sortable t-right" data-sort="first">First seen</th>
              <th class="t-right">Last seen</th>
            </tr>
          </thead>
          <tbody data-role="tbody"></tbody>
        </table>
      </div>
    </div>
  `;

  const tbody = root.querySelector('[data-role="tbody"]') as HTMLElement;
  const countEl = root.querySelector('[data-role="count"]') as HTMLElement;
  const histEl = root.querySelector('[data-role="hist"]') as HTMLElement;
  const histSub = root.querySelector('[data-role="hist-sub"]') as HTMLElement;

  // histogram over first-appearance year
  const firstYears = names.map((n) => n.span[0]);
  const bins = histogramBins(firstYears, 14);

  function paintHist(): void {
    histEl.innerHTML = '';
    histEl.appendChild(
      histogramChart(bins, {
        width: 960,
        height: 240,
        colour: 'var(--accent-primary)',
        xFmt: (v) => String(Math.round(v)),
        onPick: (i) => {
          const b = bins[i];
          binFilter = binFilter && binFilter[0] === b.lo ? null : [b.lo, b.hi];
          histSub.textContent = binFilter
            ? `Filtered to names first seen ${Math.round(binFilter[0])}–${Math.round(binFilter[1])}. Click the bar again to clear.`
            : 'Each name counted once, by the first year it appeared. Click a bar to filter the table below.';
          paintTable();
        },
      }),
    );
  }

  function filtered(): NameRec[] {
    const q = query.trim().toLowerCase();
    let rows = names;
    if (q) rows = rows.filter((n) => n.n.toLowerCase().includes(q));
    if (typeFilter !== 'all') {
      rows = rows.filter((n) => {
        const s = sexesOf(n);
        if (typeFilter === 'both') return s.boy && s.girl;
        if (typeFilter === 'boy') return s.boy && !s.girl;
        return s.girl && !s.boy;
      });
    }
    if (binFilter) rows = rows.filter((n) => n.span[0] >= binFilter![0] && n.span[0] <= binFilter![1] + 0.001);
    const dir = sortDir;
    return [...rows].sort((a, b) => {
      let c = 0;
      if (sortKey === 'name') c = a.n.localeCompare(b.n);
      else if (sortKey === 'best') c = a.bestRank - b.bestRank;
      else if (sortKey === 'total') c = a.totalCount - b.totalCount;
      else c = a.span[0] - b.span[0];
      return c * dir || a.n.localeCompare(b.n);
    });
  }

  function arrow(k: SortKey): string {
    return sortKey === k ? `<span class="arrow">${sortDir === 1 ? '▲' : '▼'}</span>` : '';
  }

  function paintTable(): void {
    const rows = filtered();
    countEl.textContent = `${formatNumber(rows.length)} names`;
    tbody.innerHTML = rows
      .slice(0, 600)
      .map(
        (n) => `<tr class="clickable" data-key="${escapeHtml(n.k)}">
          <td class="name-cell">${escapeHtml(n.n)}</td>
          <td>${typeLabel(n)}</td>
          <td class="t-num">#${n.bestRank}</td>
          <td class="t-num">${n.bestYear}</td>
          <td class="t-num">${formatNumber(n.totalCount)}</td>
          <td class="t-num">${n.span[0]}</td>
          <td class="t-num">${n.span[1]}</td>
        </tr>`,
      )
      .join('');
    // update header arrows
    root.querySelectorAll('th[data-sort]').forEach((th) => {
      const k = th.getAttribute('data-sort') as SortKey;
      th.innerHTML = th.innerHTML.replace(/<span class="arrow">.*?<\/span>/, '');
      if (k === sortKey) th.insertAdjacentHTML('beforeend', arrow(k));
    });
    if (rows.length > 600) {
      tbody.insertAdjacentHTML('beforeend', `<tr><td colspan="7" style="text-align:center;color:var(--text-tertiary)">Showing first 600 of ${formatNumber(rows.length)} — narrow with search.</td></tr>`);
    }
  }

  root.querySelectorAll('th[data-sort]').forEach((th) =>
    th.addEventListener('click', () => {
      const k = th.getAttribute('data-sort') as SortKey;
      if (sortKey === k) sortDir = (sortDir * -1) as 1 | -1;
      else {
        sortKey = k;
        sortDir = k === 'name' ? 1 : -1;
      }
      paintTable();
    }),
  );
  root.querySelectorAll('[data-opt]').forEach((b) =>
    b.addEventListener('click', () => {
      root.querySelectorAll('[data-opt]').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
      typeFilter = b.getAttribute('data-opt') as typeof typeFilter;
      paintTable();
    }),
  );
  const qInput = root.querySelector('[data-role="q"]') as HTMLInputElement;
  let timer: number | undefined;
  qInput.addEventListener('input', () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      query = qInput.value;
      paintTable();
    }, 250);
  });

  wireNameRows(tbody, ctx.openName);
  paintHist();
  paintTable();
}
